import { z, ZodError, type ZodType } from 'zod';
import type { HttpClient, QueryParams, RequestOptions } from './httpClient'

const StandardResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    data: z.unknown().optional(),
});

export const StandardSuccessResponseSchema = z.object({
    success: z.literal(true),
    message: z.string().optional(),
});

const PaginationSchema = z.object({
    cursor: z.string().nullable(),
    has_next: z.boolean(),
});

const NumberedPaginationSchema = z.object({
    page: z.number(),
    per_page: z.number(),
    total: z.number(),
    total_pages: z.number(),
    has_next: z.boolean(),
    has_prev: z.boolean(),
});

const StandardPaginatedResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().nullable().optional(),
    data: z.array(z.unknown()),
    pagination: PaginationSchema,
    meta: z.unknown().optional(),
});

const StandardNumberedPaginatedResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().nullable().optional(),
    data: z.array(z.unknown()),
    pagination: NumberedPaginationSchema,
    meta: z.unknown().optional(),
});

export type PaginationInfo = z.infer<typeof PaginationSchema>;
export type NumberedPaginationInfo = z.infer<typeof NumberedPaginationSchema>;

export type PaginatedResponse<T, TMeta = unknown, TPagination = PaginationInfo> = {
    data: T[];
    pagination: TPagination;
    meta?: TMeta;
};

export type NumberedPaginatedResponse<T, TMeta = unknown> = PaginatedResponse<T, TMeta, NumberedPaginationInfo>;

export function withQuery(url: string, query?: QueryParams) {
    if (!query) return url
    const urlObj = new URL(url)
    Object.entries(query).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            const filtered = value.filter(x => x != null).map(x => String(x));
            for (const v of filtered) {
                urlObj.searchParams.append(`${key}[]`, v);
            }
        } else if (value != null) {
            urlObj.searchParams.set(key, String(value));
        }
    })
    const newUrl = urlObj.origin + urlObj.pathname + (urlObj.search ? `?${urlObj.searchParams.toString()}` : '')
    return newUrl
}

function isFormData(value: unknown): value is FormData {
    return typeof FormData !== 'undefined' && value instanceof FormData
}

function stripContentType(headers?: Record<string, string>): Record<string, string> | undefined {
    if (!headers) return headers
    const newHeaders: Record<string, string> = {}
    for (const [k, v] of Object.entries(headers)) {
        if (k.toLowerCase() === 'content-type') continue
        newHeaders[k] = v
    }
    return newHeaders
}

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

function buildUrl<TParams>(path: string, params?: TParams, query?: QueryParams, base?: string): string {
    let url = path;

    if (params) {
        for (const [key, value] of Object.entries(params)) {
            url = url.replace(`:${key}`, String(value))
        }
    }

    const fullPath = base ? `${base}${url}` : url;
    return withQuery(fullPath, query);
}

function prepareRequestBody<TBody>(
    body: TBody | undefined,
    asFormData: boolean,
    options?: RequestOptions
): { body: unknown; options?: RequestOptions } {
    if (!asFormData) {
        return { body, options };
    }

    if (!isFormData(body)) {
        throw new Error('[createEndpoint] asFormData=true requires `body` to be a FormData')
    }

    return {
        body,
        options: {
            ...options,
            headers: {
                ...stripContentType(options?.headers),
                'Content-Type': 'multipart/form-data'
            }
        }
    };
}

async function executeRequest<TResponse, TBody>(
    client: HttpClient,
    method: Method,
    url: string,
    body: unknown,
    options?: RequestOptions
): Promise<TResponse> {
    switch (method) {
        case 'GET':
            return await client.get<TResponse>(url, options).then(r => r.data)
        case 'POST':
            return await client.post<TResponse, TBody>(url, body as TBody, options).then(r => r.data)
        case 'PUT':
            return await client.put<TResponse, TBody>(url, body as TBody, options).then(r => r.data)
        case 'PATCH':
            return await client.patch<TResponse, TBody>(url, body as TBody, options).then(r => r.data)
        case 'DELETE':
            return await client.delete<TResponse, TBody>(url, body as TBody, options).then(r => r.data)
    }
}

export function createEndpoint<TResponse, TBody = unknown, TQuery extends QueryParams = QueryParams, TParams = Record<string, string | number>>(
    client: HttpClient,
    method: Method,
    path: string,
    opts?: {
        responseSchema?: ZodType<TResponse>,
        base?: string
        params?: Array<keyof TParams>
        asFormData?: boolean
    }
) {
    return async (args?: {
        params?: TParams,
        query?: TQuery,
        body?: TBody,
        options?: RequestOptions
    }) => {
        const url = buildUrl(path, args?.params, args?.query, opts?.base);

        const { body: preparedBody, options: preparedOptions } = prepareRequestBody(
            args?.body,
            opts?.asFormData ?? false,
            args?.options
        );

        let response: unknown = await executeRequest<TResponse, TBody>(
            client,
            method,
            url,
            preparedBody,
            preparedOptions
        );

        /** check if the response follow the standard response schema **/
        try {
            const standardResponse = StandardResponseSchema.parse(response);
            if (!standardResponse.success) {
                throw new Error(standardResponse.message);
            }
            response = standardResponse.data as TResponse;

            if (!standardResponse.data) {
                // If we don't have a data, we return the success and the message
                response = standardResponse.data as TResponse ?? {
                    success: true,
                    message: standardResponse.message,
                } as TResponse;
            }
        } catch (error) {
            /**
             * If StandardResponseSchema.parse() failed (ZodError), the response
             * doesn't follow the standard format. Fall through and let the
             * responseSchema handle it (if provided).
             */
            if (error instanceof ZodError) {
                response = response as TResponse;
            } else {
                /**
                 * Re-throw all other errors, including the Error thrown when
                 * success: false (API returned an error response).
                 */
                throw error;
            }
        }

        if (opts?.responseSchema) {
            return opts.responseSchema.parse(response) as TResponse;
        }

        // Fingers crossed, don't blame me if it's not what you expected, you didn't provide a zod schema
        return response as TResponse;
    }
}

export function createEndpointWithPagination<TItem, TMeta = unknown, TBody = unknown, TQuery extends QueryParams = QueryParams, TParams = Record<string, string | number>>(
    client: HttpClient,
    method: Method,
    path: string,
    opts?: {
        itemSchema?: ZodType<TItem>,
        metaSchema?: ZodType<TMeta>,
        base?: string
        params?: Array<keyof TParams>
        asFormData?: boolean
    }
) {
    return async (args?: {
        params?: TParams,
        query?: TQuery,
        body?: TBody,
        cursor?: string,
        options?: RequestOptions
    }): Promise<PaginatedResponse<TItem, TMeta>> => {
        // Merge cursor into query params if provided
        const query: QueryParams | undefined = args?.cursor 
            ? { ...args.query, cursor: args.cursor }
            : args?.query;
            
        const url = buildUrl(path, args?.params, query, opts?.base);

        const { body: preparedBody, options: preparedOptions } = prepareRequestBody(
            args?.body,
            opts?.asFormData ?? false,
            args?.options
        );

        const response = await executeRequest<unknown, TBody>(
            client,
            method,
            url,
            preparedBody,
            preparedOptions
        );

        /** check if the response follows the standard paginated response schema **/
        try {
            const paginatedResponse = StandardPaginatedResponseSchema.parse(response);
            
            if (!paginatedResponse.success) {
                throw new Error(paginatedResponse.message ?? 'Unknown server error');
            }

            let data = paginatedResponse.data;

            // If an item schema is provided, validate each item
            if (opts?.itemSchema) {
                const arraySchema = z.array(opts.itemSchema);
                data = arraySchema.parse(data);
            }

            let meta: TMeta | undefined = undefined;
            if (opts?.metaSchema && paginatedResponse.meta !== undefined) {
                meta = opts.metaSchema.parse(paginatedResponse.meta);
            }

            return {
                data: data as TItem[],
                pagination: paginatedResponse.pagination as PaginationInfo,
                ...(meta !== undefined && { meta })
            };
        } catch (error: unknown) {
            /**
             * If it's a ZodError, the response doesn't match the expected paginated format.
             * Throw a descriptive error (leave logging to the caller).
             */
            if (error instanceof ZodError) {
                const zodError = error as ZodError;
                const responseStr = JSON.stringify(response, null, 2);
                const truncatedResponse = responseStr.length > 1000 
                    ? responseStr.substring(0, 1000) + '\n... (response truncated)'
                    : responseStr;
                
                const zodIssues = zodError.issues.map((issue: z.ZodIssue) => {
                    const path = issue.path.length > 0 ? ` at path "${issue.path.join('.')}"` : '';
                    return `  - ${issue.message}${path}`;
                }).join('\n');

                throw new Error(
                    `[${method.toUpperCase()}] ${url}\n` +
                    `Expected a paginated response format with structure:\n` +
                    `  { success: boolean, data: array, pagination: { cursor: string | null, has_next: boolean }, meta?: unknown }\n\n` +
                    `Validation errors:\n${zodIssues}\n\n` +
                    `Received response:\n${truncatedResponse}`
                );
            }
            /**
             * Re-throw all other errors, including the Error thrown when
             * success: false (API returned an error response).
             */
            throw error;
        }
    }
}

export function createEndpointWithNumberedPagination<TItem, TMeta = unknown, TBody = unknown, TQuery extends QueryParams = QueryParams, TParams = Record<string, string | number>>(
    client: HttpClient,
    method: Method,
    path: string,
    opts?: {
        itemSchema?: ZodType<TItem>,
        metaSchema?: ZodType<TMeta>,
        base?: string
        params?: Array<keyof TParams>
        asFormData?: boolean
    }
) {
    return async (args?: {
        params?: TParams,
        query?: TQuery,
        body?: TBody,
        page?: number,
        options?: RequestOptions
    }): Promise<NumberedPaginatedResponse<TItem, TMeta>> => {
        const query: QueryParams | undefined = args?.page != null
            ? { ...args.query, page: args.page }
            : args?.query;

        const url = buildUrl(path, args?.params, query, opts?.base);

        const { body: preparedBody, options: preparedOptions } = prepareRequestBody(
            args?.body,
            opts?.asFormData ?? false,
            args?.options
        );

        const response = await executeRequest<unknown, TBody>(
            client,
            method,
            url,
            preparedBody,
            preparedOptions
        );

        try {
            const paginatedResponse = StandardNumberedPaginatedResponseSchema.parse(response);

            if (!paginatedResponse.success) {
                throw new Error(paginatedResponse.message ?? 'Unknown server error');
            }

            let data = paginatedResponse.data;

            if (opts?.itemSchema) {
                const arraySchema = z.array(opts.itemSchema);
                data = arraySchema.parse(data);
            }

            let meta: TMeta | undefined = undefined;
            if (opts?.metaSchema && paginatedResponse.meta !== undefined) {
                meta = opts.metaSchema.parse(paginatedResponse.meta);
            }

            return {
                data: data as TItem[],
                pagination: paginatedResponse.pagination as NumberedPaginationInfo,
                ...(meta !== undefined && { meta })
            };
        } catch (error: unknown) {
            if (error instanceof ZodError) {
                const zodError = error as ZodError;
                const responseStr = JSON.stringify(response, null, 2);
                const truncatedResponse = responseStr.length > 1000
                    ? responseStr.substring(0, 1000) + '\n... (response truncated)'
                    : responseStr;

                const zodIssues = zodError.issues.map((issue: z.ZodIssue) => {
                    const path = issue.path.length > 0 ? ` at path "${issue.path.join('.')}"` : '';
                    return `  - ${issue.message}${path}`;
                }).join('\n');

                throw new Error(
                    `[${method.toUpperCase()}] ${url}\n` +
                    `Expected a numbered paginated response format with structure:\n` +
                    `  { success: boolean, data: array, pagination: { page: number, per_page: number, total: number, total_pages: number, has_next: boolean, has_prev: boolean }, meta?: unknown }\n\n` +
                    `Validation errors:\n${zodIssues}\n\n` +
                    `Received response:\n${truncatedResponse}`
                );
            }

            throw error;
        }
    }
}
